import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PlayerProvider } from './contexts/PlayerContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { ToastProvider } from './contexts/ToastContext';
import { HelmetProvider } from 'react-helmet-async';
import MainLayout from './layouts/MainLayout';
import { LoadingSpinner } from './components/LoadingSpinner';
import GoogleAnalytics from './components/Analytics/GoogleAnalytics';

// Every page is code-split. The app previously shipped one ~1.4MB bundle, so a
// visitor landing on Home downloaded Explore, Analytics and the whole map
// before anything rendered.
//
// MainLayout is deliberately NOT lazy: it owns the player bar, which has to stay
// mounted across navigations or audio would stop every time you change page.
const Home = lazy(() => import('./pages/Home'));
const Explore = lazy(() => import('./pages/Explore'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Map = lazy(() => import('./pages/Map'));
const Profile = lazy(() => import('./pages/Profile'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Station = lazy(() => import('./pages/Station'));

const RouteFallback: React.FC = () => (
  <div className="flex items-center justify-center py-24">
    <LoadingSpinner size="large" />
  </div>
);

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <PlayerProvider>
              <FavoritesProvider>
                <Router>
                  <GoogleAnalytics />
                  <Routes>
                    {/* Suspense sits inside the layout route so the player bar and
                        navigation stay on screen while a page chunk loads. */}
                    <Route element={<MainLayout />}>
                      <Route path="/" element={<Suspense fallback={<RouteFallback />}><Home /></Suspense>} />
                      <Route path="/explore" element={<Suspense fallback={<RouteFallback />}><Explore /></Suspense>} />
                      <Route path="/favorites" element={<Suspense fallback={<RouteFallback />}><Favorites /></Suspense>} />
                      <Route path="/map" element={<Suspense fallback={<RouteFallback />}><Map /></Suspense>} />
                      <Route path="/profile" element={<Suspense fallback={<RouteFallback />}><Profile /></Suspense>} />
                      <Route path="/analytics" element={<Suspense fallback={<RouteFallback />}><Analytics /></Suspense>} />
                      <Route path="/station/:stationId" element={<Suspense fallback={<RouteFallback />}><Station /></Suspense>} />
                    </Route>
                  </Routes>
                </Router>
              </FavoritesProvider>
            </PlayerProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;